import { DateTime } from '@ksyos/date-time';
import { Card } from '@ksyos/design-system';
import { orderBy } from 'lodash';
import { Fragment, useEffect, useState } from 'react';
import type { ServiceCode, ServiceType } from '@common/enums';
import type { IServiceEmployment } from '@frontend/components/interfaces/serviceEmployment';
import type { ServiceRightType } from '../../../../../common/interfaces/rights';
import type { IEmployment } from '../../../interfaces/employment';
import type { IPractitioner } from '../../../interfaces/practitioner';
import type { IServiceReader } from '../../../interfaces/serviceReader';
import type {
    IServiceConsumerRight,
    IServiceProviderRight,
    IServiceSupervisorRight,
} from '../../personalSettings/carePathOverview/PersonalSettingsCarePathsOverview';
import type { IServiceGroupOrganizationSettingsProps } from './ServiceGroupOrganizationSettings';
import { getDisplayName } from '../../../../../common/utils/getDisplayName';
import { fetchJson } from '../../../../store/fetch';
import { translateKey } from '../../../../translate';
import { SortDirection, toLowerCase } from '../../../../utils/sortDirection';
import { PractitionerTableContainer } from './ServiceOrganizationPractitionerTable/ServiceOrganizationPractitionerTableContainer';

export type IRight = IServiceSupervisorRightWithType | IServiceProviderRightWithType | IServiceConsumerRightWithType;

interface IServiceSupervisorRightWithType extends IServiceSupervisorRight {
    serviceName: string;
    type?: ServiceRightType.serviceSupervisor;
}

interface IServiceProviderRightWithType extends IServiceProviderRight {
    serviceCode: ServiceCode;
    serviceName: string;
    serviceType: ServiceType;
    type?: ServiceRightType.serviceProvider;
}

interface IServiceConsumerRightWithType extends IServiceConsumerRight {
    serviceName: string;
    type?: ServiceRightType.serviceConsumer;
}

interface IServiceReaderRight extends IServiceReader {
    serviceName: string;
}

interface IServiceEmploymentRight extends IServiceEmployment {
    serviceName: string;
}

export interface IServiceRights {
    serviceConsumerRights: IServiceConsumerRightWithType[];
    serviceEmploymentRights: IServiceEmploymentRight[];
    serviceProviderRights: IServiceProviderRightWithType[];
    serviceReaderRights: IServiceReaderRight[];
    serviceSupervisorRights: IServiceSupervisorRightWithType[];
}

export interface IEmploymentAndServiceRights extends IEmployment {
    activeRights: IServiceRights | null;
    activeSince: Date | undefined;
    inactiveRights: IServiceRights | null;
    practitioner: IPractitioner;
}

export function ServiceOrganizationPractitioners(props: IServiceGroupOrganizationSettingsProps) {
    const [employments, setEmployments] = useState<IEmploymentAndServiceRights[]>([]);
    const [sortDirection, setSortDirection] = useState(SortDirection.asc);
    const [sortKey, setSortKey] = useState('name');

    useEffect(() => {
        getAllPractitionersWithServiceRights();
    }, [props.organizationId, props.serviceOrganizationSettings]);

    if (!employments || !props.serviceOrganizationSettings[0]) {
        return null;
    }

    return <Card>{getPractitionerTable()}</Card>;

    async function getAllPractitionersWithServiceRights() {
        if (props.organizationId && props.serviceOrganizationSettings[0]?.service.id) {
            const allPractitionersWithServiceRightsAndCompensationPayouts = (
                await fetchJson(
                    `/api/serviceOrganizationSettings/${props.organizationId}/${props.serviceOrganizationSettings[0]?.serviceGroupName}/practitioners`,
                    'GET'
                )
            ).json;
            setEmployments(
                sort(allPractitionersWithServiceRightsAndCompensationPayouts.practitioners, sortDirection, 'name')
            );
        }
    }

    function getPractitionerTable() {
        const practitionerTablePartitioner = getPractitionerTablePartitions(employments);

        return practitionerTablePartitioner.map((partition, index) => (
            <Fragment key={`${partition.employments.toString()}-${index}`}>
                {getHeader(partition)}
                <PractitionerTableContainer
                    employments={employments}
                    episodeCategory={props.episodeCategory}
                    index={index}
                    partitionEmployments={partition.employments}
                    setEmployments={setEmployments}
                    setSortDirection={setSortDirection}
                    sort={sort}
                    sortDirection={sortDirection}
                    sortKey={sortKey}
                />
            </Fragment>
        ));
    }

    function getHeader(partition: { employments: IEmploymentAndServiceRights[]; header: JSX.Element }) {
        if (!partition.employments.length) {
            return null;
        }

        return partition.header;
    }

    function sort(employmentAndServiceRights: IEmploymentAndServiceRights[], direction: SortDirection, sortOn: string) {
        setSortKey(sortOn);
        if (sortOn === 'name') {
            return orderBy(
                employmentAndServiceRights,
                [
                    'activeRights',
                    (employmentService) =>
                        getDisplayName(employmentService.practitioner, false, 'familyNameCommaInitials'),
                    (employmentService) => employmentService.activeSince,
                ],
                [SortDirection.asc, toLowerCase(direction), sortDirection]
            );
        }
        if (sortOn === 'activeSince') {
            return orderBy(
                employmentAndServiceRights,
                [
                    'activeRights',
                    (employmentService) => employmentService.activeSince,
                    (employmentService) =>
                        getDisplayName(employmentService.practitioner, false, 'familyNameCommaInitials'),
                ],
                [SortDirection.asc, toLowerCase(direction), sortDirection]
            );
        }

        return [];
    }

    function getPractitionerTablePartitions(employmentList: IEmploymentAndServiceRights[]) {
        const presentEmployments: IEmploymentAndServiceRights[] = [];
        const pastEmployments: IEmploymentAndServiceRights[] = [];
        const futureEmployments: IEmploymentAndServiceRights[] = [];

        employmentList.forEach((employment) => {
            if (DateTime.from(employment.startDate).isBefore(new Date())) {
                if (!employment.endDate || DateTime.from(employment.endDate).isAfter(new Date())) {
                    presentEmployments.push(employment);
                } else {
                    pastEmployments.push(employment);
                }
            } else {
                futureEmployments.push(employment);
            }
        });

        const result = [
            {
                header: <Card.Header title={translateKey('organization.practitioners')} />,
                employments: presentEmployments,
            },
            {
                header: <Card.Header title={translateKey('organization.practitionersWithFutureEmployments')} />,
                employments: futureEmployments,
            },
            {
                header: <Card.Header title={translateKey('organization.practitionersWithPastEmployments')} />,
                employments: pastEmployments,
            },
        ];

        return result;
    }
}
